import { generateClient } from 'aws-amplify/api';

const client = generateClient();

// GraphQL queries and mutations (will be auto-generated after amplify push)
const listUserMessageVariants = /* GraphQL */ `
  query ListUserMessageVariants(
    $filter: ModelUserMessageVariantFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listUserMessageVariants(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        submissionId
        status
        variantTrack
        assignedAt
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const userMessageVariantsBySubmissionId = /* GraphQL */ `
  query UserMessageVariantsBySubmissionId(
    $submissionId: ID!
    $status: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelUserMessageVariantFilterInput
    $limit: Int
    $nextToken: String
  ) {
    userMessageVariantsBySubmissionIdAndStatus(
      submissionId: $submissionId
      status: $status
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        submissionId
        status
        variantTrack
        assignedAt
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const createUserMessageVariant = /* GraphQL */ `
  mutation CreateUserMessageVariant(
    $input: CreateUserMessageVariantInput!
  ) {
    createUserMessageVariant(input: $input) {
      id
      submissionId
      status
      variantTrack
      assignedAt
      createdAt
      updatedAt
    }
  }
`;

const updateUserMessageVariant = /* GraphQL */ `
  mutation UpdateUserMessageVariant(
    $input: UpdateUserMessageVariantInput!
  ) {
    updateUserMessageVariant(input: $input) {
      id
      submissionId
      status
      variantTrack
      assignedAt
      createdAt
      updatedAt
    }
  }
`;

const deleteUserMessageVariant = /* GraphQL */ `
  mutation DeleteUserMessageVariant(
    $input: DeleteUserMessageVariantInput!
  ) {
    deleteUserMessageVariant(input: $input) {
      id
    }
  }
`;

/**
 * Generate a random variant track number (1-4)
 * @returns {number} Random track number between 1 and 4
 */
export const generateRandomTrack = () => {
  return Math.floor(Math.random() * 4) + 1;
};

/**
 * Get count of assignments per track for balanced distribution
 * @returns {Promise<Object>} Object with track numbers as keys and counts as values
 */
export const getTrackDistribution = async () => {
  try {
    let allItems = [];
    let nextToken = null;

    do {
      const result = await client.graphql({
        query: listUserMessageVariants,
        variables: {
          limit: 1000,
          nextToken
        }
      });

      const items = result.data.listUserMessageVariants?.items || [];
      allItems = [...allItems, ...items];
      nextToken = result.data.listUserMessageVariants?.nextToken;
    } while (nextToken);

    // Count assignments per track
    const distribution = { 1: 0, 2: 0, 3: 0, 4: 0 };
    allItems.forEach(item => {
      if (item.variantTrack >= 1 && item.variantTrack <= 4) {
        distribution[item.variantTrack]++;
      }
    });

    console.log('[INFO] Current track distribution:', distribution);
    return distribution;
  } catch (error) {
    console.error('[ERROR] Error getting track distribution:', error);
    throw error;
  }
};

/**
 * Generate a balanced variant track - assigns to least used track
 * Falls back to random if there's an error
 * @returns {Promise<number>} Track number between 1 and 4
 */
export const generateBalancedTrack = async () => {
  try {
    const distribution = await getTrackDistribution();
    
    // Find the minimum count
    const minCount = Math.min(...Object.values(distribution));
    
    // Get all tracks with the minimum count
    const leastUsedTracks = Object.entries(distribution)
      .filter(([_, count]) => count === minCount)
      .map(([track, _]) => parseInt(track));
    
    // Randomly pick one of the least used tracks (for tie-breaking)
    const selectedTrack = leastUsedTracks[Math.floor(Math.random() * leastUsedTracks.length)];
    
    console.log(`[INFO] Balanced assignment: selected track ${selectedTrack} (least used: ${leastUsedTracks.join(', ')})`);
    return selectedTrack;
  } catch (error) {
    console.error('[WARN] Failed to get balanced track, falling back to random:', error);
    return generateRandomTrack();
  }
};

/**
 * Get user's variant assignment for a specific status
 * @param {string} submissionId - The submission ID
 * @param {string} status - The user's current status
 * @returns {Promise<Object|null>} The variant assignment or null
 */
export const getVariantForSubmissionAndStatus = async (submissionId, status) => {
  try {
    const result = await client.graphql({
      query: userMessageVariantsBySubmissionId,
      variables: {
        submissionId,
        status: { eq: status }
      }
    });

    const items = result.data.userMessageVariantsBySubmissionIdAndStatus?.items || [];
    return items.length > 0 ? items[0] : null;
  } catch (error) {
    console.error('[ERROR] Error fetching variant for submission:', error);
    throw error;
  }
};

/**
 * Get all variant assignments for a submission
 * @param {string} submissionId - The submission ID
 * @returns {Promise<Array>} Array of variant assignments
 */
export const getAllVariantsForSubmission = async (submissionId) => {
  try {
    const result = await client.graphql({
      query: userMessageVariantsBySubmissionId,
      variables: {
        submissionId
      }
    });

    return result.data.userMessageVariantsBySubmissionIdAndStatus?.items || [];
  } catch (error) {
    console.error('[ERROR] Error fetching all variants for submission:', error);
    throw error;
  }
};

/**
 * Assign a variant track to a user for their current status
 * @param {string} submissionId - The submission ID
 * @param {string} status - The user's current status
 * @param {number} variantTrack - Optional specific track (1-4), random if not provided
 * @returns {Promise<Object>} The created variant assignment
 */
export const assignVariantTrack = async (submissionId, status, variantTrack = null) => {
  try {
    // DynamoDB doesn't allow empty strings for index keys - use default if empty
    const effectiveStatus = status && status.trim() ? status : 'Unknown';
    
    // Use balanced assignment for even distribution across tracks
    const track = variantTrack || await generateBalancedTrack();
    
    const result = await client.graphql({
      query: createUserMessageVariant,
      variables: {
        input: {
          submissionId,
          status: effectiveStatus,
          variantTrack: track,
          assignedAt: new Date().toISOString()
        }
      }
    });

    console.log(`[INFO] Assigned variant track ${track} to submission ${submissionId} for status ${status}`);
    return result.data.createUserMessageVariant;
  } catch (error) {
    console.error('[ERROR] Error assigning variant track:', error);
    throw error;
  }
};

/**
 * Get or assign a variant track for a user
 * If user already has ANY variant assigned, return that track (user stays on their track)
 * If not, assign a new balanced variant
 * @param {string} submissionId - The submission ID
 * @param {string} status - The user's current status
 * @returns {Promise<Object>} The variant assignment (existing or new)
 */
export const getOrAssignVariant = async (submissionId, status) => {
  try {
    // First, check if user already has ANY variant assigned (regardless of status)
    const allVariants = await getAllVariantsForSubmission(submissionId);
    
    if (allVariants.length > 0) {
      // User already has a track - reuse it
      const existingTrack = allVariants[0].variantTrack;
      console.log(`[INFO] User already has track ${existingTrack} assigned - reusing for status "${status}"`);
      
      // Check if we already have a record for this specific status
      const existingForStatus = allVariants.find(v => v.status === status);
      if (existingForStatus) {
        return existingForStatus;
      }
      
      // Create a new record for this status but with the SAME track
      return await assignVariantTrack(submissionId, status, existingTrack);
    }

    // No existing variant - assign a new balanced track
    console.log(`[INFO] No existing track for submission ${submissionId} - assigning new balanced track`);
    return await assignVariantTrack(submissionId, status);
  } catch (error) {
    console.error('[ERROR] Error in getOrAssignVariant:', error);
    throw error;
  }
};

/**
 * Update an existing variant assignment
 * @param {string} id - The variant assignment ID
 * @param {number} variantTrack - The new track number
 * @returns {Promise<Object>} The updated variant assignment
 */
export const updateVariantAssignment = async (id, variantTrack) => {
  try {
    const result = await client.graphql({
      query: updateUserMessageVariant,
      variables: {
        input: {
          id,
          variantTrack,
          assignedAt: new Date().toISOString()
        }
      }
    });

    return result.data.updateUserMessageVariant;
  } catch (error) {
    console.error('[ERROR] Error updating variant assignment:', error);
    throw error;
  }
};

/**
 * Delete a variant assignment
 * @param {string} id - The variant assignment ID
 * @returns {Promise<Object>} The deleted variant
 */
export const deleteVariantAssignment = async (id) => {
  try {
    const result = await client.graphql({
      query: deleteUserMessageVariant,
      variables: {
        input: { id }
      }
    });

    return result.data.deleteUserMessageVariant;
  } catch (error) {
    console.error('[ERROR] Error deleting variant assignment:', error);
    throw error;
  }
};

/**
 * Get variant assignment history for a submission
 * Useful for debugging and tracking variant changes
 * @param {string} submissionId - The submission ID
 * @returns {Promise<Array>} Array of all variant assignments sorted by assignedAt
 */
export const getVariantHistory = async (submissionId) => {
  try {
    const variants = await getAllVariantsForSubmission(submissionId);
    
    // Sort by assignedAt descending (most recent first)
    return variants.sort((a, b) => 
      new Date(b.assignedAt) - new Date(a.assignedAt)
    );
  } catch (error) {
    console.error('[ERROR] Error fetching variant history:', error);
    throw error;
  }
};

/**
 * Assign a random variant track to a user - creates new or updates existing
 * Ensures only ONE entry per submissionId (uses 'Appointment' as status)
 * @param {string} submissionId - The submission ID
 * @returns {Promise<Object>} The variant assignment with the random track
 */
export const assignRandomVariant = async (submissionId) => {
  try {
    const randomTrack = generateRandomTrack();
    const status = 'Appointment'; // Single status for appointment bookings
    
    // Check if user already has a variant entry
    const existingVariants = await getAllVariantsForSubmission(submissionId);
    
    if (existingVariants.length > 0) {
      // Update the first existing entry with new random track
      const existingEntry = existingVariants[0];
      console.log(`[INFO] Updating existing variant entry for ${submissionId} with random track ${randomTrack}`);
      
      const updated = await updateVariantAssignment(existingEntry.id, randomTrack);
      return updated;
    } else {
      // Create new entry
      console.log(`[INFO] Creating new variant entry for ${submissionId} with random track ${randomTrack}`);
      
      const result = await client.graphql({
        query: createUserMessageVariant,
        variables: {
          input: {
            submissionId,
            status,
            variantTrack: randomTrack,
            assignedAt: new Date().toISOString()
          }
        }
      });
      
      return result.data.createUserMessageVariant;
    }
  } catch (error) {
    console.error('[ERROR] Error assigning random variant:', error);
    throw error;
  }
};
